/**
 * デッキの一覧（/decks。Issue #953 / #954）。1 行＝1 デッキ。名前・イラスト・スリーブ・手元にあるかはここで直す。
 * 行を押すとデッキのページ（/decks/:deckId）。デッキを作るのは名前と持ち主だけで、レシピは編集ページ（/decks/:deckId/edit）で入れる。
 * 抽選は別のタブ（/lottery）。持ち主はプレイヤーのアイコンで切り替える。絞り込みは端末に覚える（localStorage `deckList.view`）。
 * 「要確認」のタブ（/decks/check）: 試合に割り当てられているのに、レシピのメインが 40 枚に足りない版（`GetIncompleteDeckAssignments`）。
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import { DeckImageSelectModal } from '../components/deck/deckParts';
import DeckNav from '../components/deck/DeckNav';

const HAVE_FILTERS = [['all', 'すべて'], ['have', '手元にある'], ['recipe', 'レシピだけ']];
const SORTS = [['name', '名前順'], ['updated', '更新順']];

const VIEW_KEY = 'deckList.view';
const DEFAULT_VIEW = { query: '', player: '', have: 'all', sort: 'name' };
function readView() {
  try { return { ...DEFAULT_VIEW, ...JSON.parse(localStorage.getItem(VIEW_KEY) || '{}') }; } catch { return DEFAULT_VIEW; }
}

/** 一覧の絞り込みと並べ替え（純関数） */
export function filterDecks(decks, { query = '', player = '', have = 'all', sort = 'name' } = {}) {
  const q = query.trim().toLowerCase();
  const rows = decks.filter((d) => (!player || d.player === player)
    && (have === 'all' || (have === 'have' ? d.enable !== false : d.enable === false))
    && (!q || String(d.name ?? '').toLowerCase().includes(q)));
  return rows.slice().sort(sort === 'updated'
    ? (a, b) => String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? ''))
    : (a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), 'ja'));
}

/** 持ち主の切り替え: 「全員」とプレイヤーのアイコン（画像が無ければ色の丸に名前の頭文字）。押した人だけ、もう一度押すと全員 */
export function PlayerFilter({ players, value, onChange }) {
  const size = 40;
  const ring = (on) => ({ width: size, height: size, borderRadius: '50%', padding: 0, cursor: 'pointer', overflow: 'hidden',
    border: on ? '3px solid #e94560' : '3px solid transparent', background: '#1a1a2e', flexShrink: 0 });
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} data-testid="deck-player">
      <button type="button" style={{ ...ring(!value), color: '#ddd', fontSize: '0.7rem' }} onClick={() => onChange('')} title="全員">全員</button>
      {players.map((p) => (
        <button key={p.name} type="button" style={ring(value === p.name)} title={p.kanji || p.name} data-testid={`player-${p.name}`}
          onClick={() => onChange(value === p.name ? '' : p.name)}>
          {p.image_thumbnail_url
            ? <img src={p.image_thumbnail_url} alt={p.kanji || p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center',
              background: p.color || '#3a5080', color: '#fff', fontWeight: 'bold' }}>{(p.kanji || p.name || '?').slice(0, 1)}</span>}
        </button>
      ))}
    </div>
  );
}

/** 要確認: 試合に割り当てられているのにレシピが足りない版 */
function IncompleteAssignments({ rows, players }) {
  const label = (name) => players.find((p) => p.name === name)?.kanji || name || '—';
  if (!rows) return <p className="loading">読み込み中...</p>;
  if (!rows.length) return <p className="loading">ありません（試合に割り当てられたデッキは全部 40 枚以上あります）</p>;
  return (
    <div className="table-wrap">
      <table className="table" data-testid="deck-incomplete">
        <thead><tr><th>デュエル</th><th>席</th><th>デッキ</th><th>枚数</th><th></th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.duel_id}-${r.seat}`}>
              <td><Link to={`/duels/${r.duel_id}`}>{r.duel_name}</Link><span className="card-meta">（{r.games} 試合）</span></td>
              <td>P{r.seat}・{label(r.player)}</td>
              <td><Link to={`/decks/${r.deck_id}`}>{r.deck_name}</Link> v{r.version_no}</td>
              <td style={{ color: '#e89a9a' }}>{r.main_count}{r.extra_count > 0 ? `+${r.extra_count}` : ''}</td>
              <td><Link className="btn btn-secondary btn-sm" to={`/decks/${r.deck_id}/edit?version=${r.version_id}`}>レシピを編集</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreateDeckModal({ players, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [player, setPlayer] = useState(players[0]?.name ?? '');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      onCreated(await api.project('CreateDeck', { name: name.trim(), player }));
    } catch (err) {
      alert('作れませんでした: ' + err.message);
      setSaving(false);
    }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">デッキを作る</h2>
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">デッキ名</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">持ち主（あとから変えられません）</label>
            <select className="form-select" value={player} onChange={(e) => setPlayer(e.target.value)}>
              {players.map((p) => <option key={p.name} value={p.name}>{p.kanji || p.name}</option>)}
            </select>
          </div>
          <p className="card-meta">作ったら編集ページでレシピを入れます（カードを探す・YDK から追加）。</p>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>キャンセル</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>{saving ? '作成中...' : '作って編集へ'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DeckManagement({ tab = 'list' }) {
  usePageTitle('デッキ');
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [players, setPlayers] = useState([]);
  const [view, setView] = useState(readView);
  // 絞り込み（持ち主・検索語・手元・並べ方）は端末に覚える。デッキのページから戻ってきても同じ絞り込みで開く
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, JSON.stringify(view)); } catch { /* 覚えられなくても絞り込みは効く */ } }, [view]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingName, setEditingName] = useState(null);   // { id, value }
  const [editingImage, setEditingImage] = useState(null);  // { deck, kind: 'illust' | 'sleeve' }
  const [incomplete, setIncomplete] = useState(null);

  const load = useCallback(() => api.project('GetDeckList').then((rows) => setDecks(rows ?? [])), []);
  useEffect(() => {
    load();
    api.project('GetPlayersList').then(setPlayers);
    api.project('GetIncompleteDeckAssignments').then((r) => setIncomplete(r ?? [])).catch(() => setIncomplete([]));
  }, [load]);

  const patch = (id, values) => setDecks((prev) => prev.map((d) => (d.id === id ? { ...d, ...values } : d)));

  const toggleHave = async (deck) => {
    try {
      await api.project('SetDeckEnable', { deck_id: deck.id, enable: deck.enable === false });
      patch(deck.id, { enable: deck.enable === false });
    } catch (err) { alert('変えられませんでした: ' + err.message); }
  };

  const saveName = async (deck) => {
    const value = editingName?.value.trim();
    setEditingName(null);
    if (!value || value === deck.name) return;
    try {
      await api.project('SetDeckName', { deck_id: deck.id, name: value });
      patch(deck.id, { name: value });
    } catch (err) { alert('変えられませんでした: ' + err.message); }
  };

  const rows = filterDecks(decks, view);
  const set = (k) => (e) => setView((v) => ({ ...v, [k]: e.target.value }));
  const stop = (e) => e.stopPropagation();

  return (
    <>
    {/* 帯は書き出し・撮影・カード管理と同じ（ページの上に .subnav） */}
    <DeckNav incompleteCount={incomplete ? incomplete.length : null} />
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">デッキ管理</h1>
        <div className="actions">
          <button className="btn btn-primary" onClick={() => { if (tab !== 'list') navigate('/decks'); setShowCreate(true); }}>＋ デッキを作る</button>
        </div>
      </div>

      {tab === 'check' && (
        <>
          <p className="card-meta" style={{ marginBottom: 8 }}>試合に割り当てられているのに、レシピのメインデッキが 40 枚に足りないものです。</p>
          <IncompleteAssignments rows={incomplete} players={players} />
        </>
      )}

      {tab === 'list' && <>
      <div style={{ marginBottom: 10 }}>
        <PlayerFilter players={players} value={view.player} onChange={(player) => setView((v) => ({ ...v, player }))} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <input className="form-input" style={{ width: 200 }} placeholder="デッキ名で探す" value={view.query} onChange={set('query')}
          data-testid="deck-search" inputMode="search" />
        <select className="form-select" style={{ width: 'auto' }} value={view.have} onChange={set('have')} data-testid="deck-have">
          {HAVE_FILTERS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select className="form-select" style={{ width: 'auto' }} value={view.sort} onChange={set('sort')} data-testid="deck-sort">
          {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <span className="card-meta">{rows.length} / {decks.length} デッキ</span>
      </div>

      {showCreate && (
        <CreateDeckModal players={players} onClose={() => setShowCreate(false)}
          onCreated={(r) => navigate(`/decks/${r.deck_id}/edit`)} />
      )}
      {editingImage && (
        <DeckImageSelectModal
          deck={editingImage.deck}
          {...(editingImage.kind === 'illust'
            ? { field: 'deck_illust', label: 'デッキイラスト', listAction: 'GetImageDeckIllustList', uploadAction: 'UploadDeckIllust', s3Prefix: 'deckImages/' }
            : { field: 'sleeve_image', label: 'スリーブ', listAction: 'GetImageSleeveList', uploadAction: 'UploadSleeveImage', s3Prefix: 'sleeves/' })}
          onClose={() => setEditingImage(null)} onSaved={() => { setEditingImage(null); load(); }}
        />
      )}

      {rows.length === 0 && <p className="loading">デッキがありません</p>}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>イラスト</th><th>スリーブ</th><th>デッキ名</th><th>持ち主</th><th>手元</th></tr>
          </thead>
          <tbody>
            {rows.map((deck) => (
              <tr key={deck.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/decks/${deck.id}`)} data-testid={`deck-row-${deck.id}`}>
                <td onClick={stop}>
                  <div style={{ width: 50, height: 50, overflow: 'hidden', borderRadius: 4, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
                    title="押してイラストを変える" onClick={() => setEditingImage({ deck, kind: 'illust' })}>
                    {deck.deck_illust_thumbnail_url
                      ? <img src={deck.deck_illust_thumbnail_url} alt={deck.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ color: '#555', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>＋</span>}
                  </div>
                </td>
                <td onClick={stop}>
                  <div style={{ width: 38, height: 50, overflow: 'hidden', borderRadius: 4, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
                    title="押してスリーブを変える" onClick={() => setEditingImage({ deck, kind: 'sleeve' })}>
                    {deck.sleeve_image_thumbnail_url
                      ? <img src={deck.sleeve_image_thumbnail_url} alt={deck.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ color: '#555', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>＋</span>}
                  </div>
                </td>
                <td>
                  {editingName?.id === deck.id ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={stop}>
                      <input className="form-input" style={{ padding: '2px 6px', fontSize: '0.9rem' }} value={editingName.value} autoFocus
                        onChange={(e) => setEditingName({ id: deck.id, value: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveName(deck); if (e.key === 'Escape') setEditingName(null); }} />
                      <button className="btn btn-sm btn-primary" style={{ padding: '1px 6px' }} onClick={() => saveName(deck)}>保存</button>
                      <button className="btn btn-sm btn-secondary" style={{ padding: '1px 6px' }} onClick={() => setEditingName(null)}>✕</button>
                    </span>
                  ) : (
                    <>
                      <strong>{deck.name}</strong>
                      <button className="btn btn-sm btn-secondary" style={{ marginLeft: 6, padding: '0 6px', fontSize: '0.7rem' }} title="名前を変える"
                        onClick={(e) => { stop(e); setEditingName({ id: deck.id, value: deck.name }); }}>✎</button>
                      <span style={{ marginLeft: 6, fontSize: '0.75rem', color: '#888' }}>
                        {deck.main_card_count}{deck.extra_card_count > 0 ? `+${deck.extra_card_count}` : ''}枚
                        {deck.latest_version_no ? `・v${deck.latest_version_no}` : ''}
                      </span>
                    </>
                  )}
                </td>
                <td><span className="tag">{deck.player}</span></td>
                <td onClick={stop}>
                  <button className={`btn btn-sm ${deck.enable !== false ? 'btn-primary' : 'btn-secondary'}`} onClick={() => toggleHave(deck)}
                    title="実物が手元にあるか（抽選は手元にあるデッキから）">
                    {deck.enable !== false ? '手元にある' : 'レシピだけ'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}
    </div>
    </>
  );
}
